import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement[]} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false);
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '#predictive-search-products, ' +
          '[id*="predictive-search-results-products"], ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (
          container.classList.contains('predictive-search-results__wrapper-products') ||
          container.id?.includes('products')
        ) {
          return Array.from(
            container.querySelectorAll('.predictive-search-results__card, [data-product-id], .predictive-search__item, slideshow-slide')
          );
        }
        return Array.from(
          container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card, .predictive-search__item')
        );
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Helper to reindex slider children so aria labels and slideshow calculations remain consistent.
   * @param {HTMLElement} slidesContainer
   */
  #reindexSlides(slidesContainer) {
    const sectionId = this.dataset.sectionId || '';
    const slides = Array.from(slidesContainer.children).filter((el) => el instanceof HTMLElement);
    const total = slides.length;

    slides.forEach((slide, index) => {
      const idx = index + 1;
      slide.id = `Slide-${sectionId}-${idx}`;
      slide.setAttribute('aria-label', `${idx} / ${total}`);
      slide.setAttribute('data-search-result-index', `search-results-product-${idx}`);
    });
  }

  /**
   * Fetch search results executing both predictive search and storefront search in parallel,
   * merging unique storefront search products with predictive search results within the native DOM structure.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const predictiveUrl = new URL(Theme.routes.predictive_search_url, location.origin);
    predictiveUrl.searchParams.set('q', searchTerm);
    predictiveUrl.searchParams.set('resources[limit_scope]', 'each');

    const storefrontUrl = new URL(Theme.routes.search_url, location.origin);
    storefrontUrl.searchParams.set('q', searchTerm);
    storefrontUrl.searchParams.set('resources[type]', 'product');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const [predictiveMarkup, storefrontMarkup] = await Promise.all([
        sectionRenderer.getSectionHTML(this.dataset.sectionId, false, predictiveUrl),
        sectionRenderer.getSectionHTML(this.dataset.sectionId, false, storefrontUrl)
      ]);

      if (abortController.signal.aborted) return;

      const parser = new DOMParser();
      const predictiveDoc = parser.parseFromString(predictiveMarkup || '', 'text/html');
      const storefrontDoc = parser.parseFromString(storefrontMarkup || '', 'text/html');

      const getCardKey = (item) => {
        const id = item.dataset.productId || item.querySelector('[data-product-id]')?.dataset.productId;
        if (id) return id;
        const link = item.querySelector('a[href*="/products/"]') || item.querySelector('a');
        if (link && link.getAttribute('href')) {
          const href = link.getAttribute('href');
          const match = href.match(/\/products\/([^?#]+)/);
          if (match && match[1]) return match[1];
          return href.split('?')[0].split('#')[0];
        }
        return item.textContent.trim();
      };

      const predictiveProductsContainer = predictiveDoc.querySelector('#predictive-search-products') ||
                                           predictiveDoc.querySelector('.predictive-search-results__wrapper-products');

      const storefrontProductsContainer = storefrontDoc.querySelector('#predictive-search-products') ||
                                            storefrontDoc.querySelector('.predictive-search-results__wrapper-products');

      const predictiveInner = predictiveDoc.querySelector('.predictive-search-results__inner');

      if (predictiveProductsContainer) {
        const targetSlidesContainer = predictiveProductsContainer.querySelector('.slideshow__slides') ||
                                       predictiveProductsContainer.querySelector('.predictive-search-results__list') ||
                                       predictiveProductsContainer;

        const existingItems = Array.from(targetSlidesContainer.children);
        const existingKeys = new Set(existingItems.map(getCardKey));

        if (storefrontProductsContainer) {
          const storefrontSlidesContainer = storefrontProductsContainer.querySelector('.slideshow__slides') ||
                                             storefrontProductsContainer.querySelector('.predictive-search-results__list') ||
                                             storefrontProductsContainer;

          const storefrontItems = Array.from(storefrontSlidesContainer.children);

          const exploreBtn = predictiveProductsContainer.querySelector('.predictive-search-results__button');

          storefrontItems.forEach((item) => {
            const key = getCardKey(item);
            if (key && !existingKeys.has(key)) {
              existingKeys.add(key);
              const clone = item.cloneNode(true);
              if (exploreBtn && exploreBtn.parentElement === targetSlidesContainer) {
                targetSlidesContainer.insertBefore(clone, exploreBtn);
              } else {
                targetSlidesContainer.appendChild(clone);
              }
            }
          });
        }

        this.#reindexSlides(targetSlidesContainer);
      } else if (storefrontProductsContainer && predictiveInner) {
        const noResultsEl = predictiveDoc.querySelector('.predictive-search-results__no-results');
        if (noResultsEl) {
          noResultsEl.remove();
        }

        const targetSlidesContainer = storefrontProductsContainer.querySelector('.slideshow__slides') ||
                                       storefrontProductsContainer.querySelector('.predictive-search-results__list') ||
                                       storefrontProductsContainer;

        if (targetSlidesContainer) {
          this.#reindexSlides(targetSlidesContainer);
        }

        const queriesWrapper = predictiveDoc.querySelector('.predictive-search-results__wrapper-queries');
        if (queriesWrapper) {
          queriesWrapper.after(storefrontProductsContainer.cloneNode(true));
        } else {
          predictiveInner.prepend(storefrontProductsContainer.cloneNode(true));
        }
      }

      const rootResult = predictiveDoc.querySelector('#predictive-search-results') || predictiveDoc.body.firstElementChild;
      const finalMarkup = rootResult ? rootResult.outerHTML : (predictiveMarkup || storefrontMarkup);

      if (abortController.signal.aborted) return;

      morph(predictiveSearchResults, finalMarkup);
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}